import React from 'react';
import {
  ArrowDown,
  ArrowRight,
  Building2,
  ClipboardCheck,
  Code2,
  FileText,
  GitBranch,
  Layers,
  ListChecks,
  MapPin,
  Phone,
  UserCheck,
  UserRound,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  CapabilityGrid,
  CONTACT_PATH,
  DEMO_PATH,
  EvidenceBoard,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const CAPABILITIES = [
  { title: 'Multi-role hiring', detail: 'Run more than one role on the same hiring layer.', icon: Layers },
  { title: 'Role-based workflows', detail: 'Each role can use the checks that the job actually needs.', icon: GitBranch },
  { title: 'AI hiring agents', detail: 'Agents cover repetitive screening and interview work.', icon: ClipboardCheck },
  { title: 'Human-in-the-loop decisions', detail: 'A person still makes the hiring decision.', icon: UserCheck },
  { title: 'Hiring analytics', detail: 'Recruiters can follow the same workflow across roles.', icon: ListChecks },
  { title: 'Enterprise integrations', detail: 'The workflow is meant to sit with tools a team already uses.', icon: Building2 },
  { title: 'Centralized candidate intelligence', detail: 'Resume, assessment, and interview records in one review.', icon: FileText },
  { title: 'Standardized assessment', detail: 'The same kind of check can be used across teams.', icon: ClipboardCheck },
];

const SCALE = [
  { title: 'Teams', icon: Users },
  { title: 'Roles', icon: GitBranch },
  { title: 'Locations', icon: MapPin },
  { title: 'Recruiters', icon: UserRound },
];

const WORKFLOW = [
  { title: 'Role Setup', detail: 'A team sets the role and the criteria before screening starts.', icon: FileText },
  { title: 'Screening', detail: 'Resumes are read against that role.', icon: ClipboardCheck },
  { title: 'Assessment', detail: 'The assessment matches the job, not one template for every hire.', icon: ListChecks },
  { title: 'Interview', detail: 'A structured interview is saved for the team to review.', icon: Video },
  { title: 'Candidate Intelligence', detail: 'The records stay together for a recruiter to read.', icon: FileText },
  { title: 'Human Decision', detail: 'A recruiter or hiring manager decides who moves forward.', icon: UserCheck },
];

const AI_WORK = [
  'Screening',
  'Repetitive interviews',
  'Assessments',
  'Candidate evidence',
  'Workflow consistency',
];

const HUMAN_WORK = [
  'Role criteria',
  'Recruiter review',
  'Hiring-manager judgment',
  'Final decisions',
];

const PRODUCTS = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'How the resume fits this role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'Screening Assessment',
    helps: 'How the resume scores against the job description.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, communication, and role-based skills.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Coding Assessment',
    helps: 'Whether technical candidates can do the work.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'AI Call Screening',
    helps: 'How a candidate answers role questions on a structured call.',
    href: '/hirewithus/ai-call-screening',
    icon: Phone,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A structured AI interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

const HERO_STEPS = [
  { label: 'Teams, roles, and locations', note: 'More than one hiring need' },
  { label: 'Shared SearchJob hiring layer', note: 'One operating path' },
  { label: 'Screen, assess, interview', note: 'The same kinds of evidence' },
  { label: 'Recruiter decision', note: 'A person decides' },
];

export default function EnterpriseHiring() {
  useSEO({
    title: 'Enterprise AI Recruitment Platform | SearchJob.ai',
    description:
      'Enterprise AI recruitment for multi-role hiring, standardized workflows, assessments, interviews, analytics and integrations.',
    canonical: 'https://searchjob.ai/solutions/enterprise',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <CapabilitySection />
        </ScrollReveal>
        <ScrollReveal>
          <Scale />
        </ScrollReveal>
        <ScrollReveal>
          <Workflow />
        </ScrollReveal>
        <ScrollReveal>
          <Governance />
        </ScrollReveal>
        <ScrollReveal>
          <Intelligence />
        </ScrollReveal>
        <ScrollReveal>
          <Products />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
            Enterprise hiring
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Enterprise Hiring. <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Without Enterprise Complexity.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Standardize hiring across teams, functions and geographies while keeping humans in control of every hiring decision.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={CONTACT_PATH}>
                Talk to Enterprise Hiring
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href={DEMO_PATH}>Book an Enterprise Demo</a>
            </Button>
          </div>
        </div>
        <ControlPath />
      </div>
    </section>
  );
}

function ControlPath() {
  return (
    <div className="relative self-start" aria-hidden="true">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shared hiring path</p>
      <ol className="space-y-3">
        {HERO_STEPS.map((step, index) => (
          <li key={step.label}>
            <div className="flex items-center gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="block text-sm text-muted-foreground">{step.note}</span>
              </span>
            </div>
            {index < HERO_STEPS.length - 1 ? <ArrowDown className="mx-auto my-1 h-4 w-4 text-primary/70" /> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function CapabilitySection() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Enterprise capabilities"
        title="One hiring layer for many teams."
        copy="The same platform can support more than one role. People still control the decision."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CAPABILITIES.map((item) => (
          <article
            key={item.title}
            className="group rounded-2xl border border-primary bg-primary/5 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none"
          >
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary transition-colors duration-200 group-hover:border-primary">
              <item.icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
          </article>
        ))}
      </div>
    </Section>
  );
}

function Scale() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Built for scale"
        title="Many positions. One consistent workflow."
        copy="Manage a large set of open roles while recruiters follow the same evaluation path."
      />
      <div className="mx-auto max-w-5xl rounded-3xl border border-primary bg-primary/5 p-5 sm:p-6">
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SCALE.map((item) => (
            <li key={item.title} className="flex min-h-11 items-center gap-3 rounded-xl border border-primary/20 bg-background/70 px-4 py-3">
              <item.icon className="h-5 w-5 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">{item.title}</span>
            </li>
          ))}
        </ul>
        <span className="industry-flow-line mx-auto my-4 block h-5 w-px" aria-hidden="true" />
        <p className="mx-auto max-w-xl rounded-2xl border border-primary/30 bg-primary/10 px-5 py-3 text-center text-sm font-semibold text-foreground">
          One shared hiring operating layer
        </p>
        <span className="industry-flow-line mx-auto my-4 block h-5 w-px" aria-hidden="true" />
        <p className="mx-auto max-w-xl text-center text-sm font-medium leading-relaxed text-foreground">
          Consistent evaluation, evidence, and a human decision
        </p>
      </div>
    </Section>
  );
}

function Workflow() {
  return (
    <Section id="enterprise-workflow" muted>
      <SectionIntro
        eyebrow="Standardized hiring workflow"
        title="The same stages. Criteria that still fit the role."
        copy="Select a step. Teams can share the path without using the same check for every job."
      />
      <IndustryWorkflow steps={WORKFLOW} />
    </Section>
  );
}

function Governance() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Governance"
        title="AI supports the workflow. Humans make final hiring decisions."
        copy="SearchJob.ai collects evidence. It does not make the hiring decision."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <WorkList title="AI supports" items={AI_WORK} />
        <WorkList title="Your team controls" items={HUMAN_WORK} />
      </div>
    </Section>
  );
}

function WorkList({ title, items }) {
  return (
    <article className="rounded-2xl border border-primary bg-primary/5 p-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function Intelligence() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Candidate intelligence"
        title="Several records. One review."
        copy="SearchJob.ai keeps resume, assessment, interview, and behaviour records for a recruiter to read. This is an example, not a live candidate."
      />
      <EvidenceBoard
        profileMark="ER"
        profileTitle="Enterprise role"
        profileNote="Sample review"
        signals={['Resume', 'Assessment', 'Interview', 'Behaviour']}
        reviewLabel="Records for review"
        nextLabel="Next: recruiter review"
        connectedFlow
      />
    </Section>
  );
}

function Products() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Existing capabilities"
        title="Products already on SearchJob.ai."
        copy="These are the tools behind the enterprise workflow."
      />
      <CapabilityGrid items={PRODUCTS} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Bring SearchJob into your existing recruitment ecosystem."
      copy="Create a standardized hiring workflow across teams while keeping recruiters and hiring managers in control."
      primaryLabel="Talk to Enterprise Hiring"
      primaryHref={CONTACT_PATH}
      secondaryLabel="Book an Enterprise Demo"
      secondaryHref={DEMO_PATH}
    />
  );
}
